import { EmeraldErrorModal } from 'src/common/api/emerald-error';
import { EntryMethod } from 'src/common/enumerations/entry-method';
import { ITender } from 'src/common/interfaces/dtos/ITender';
import { store } from 'src/common/redux/configure-store';
import { endPaymentSession, inPaymentSession } from 'src/devices/payment/pinpad-operations';
import { preMakeTenderPayment } from 'src/home-page/business-logic/pre-make-tender-payment';
import { updateTransactionStatus } from 'src/home-page/business-logic/update-cart-status';
import { CartStatus } from 'src/items-list/business-logic/service/enumerations/cart-status';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import { resetSalesKeypadState } from 'src/sales-keypad/business-logic/sales-keypad-operations';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';
import { showProcessTender } from '../components/process-tender/process-tender';
import { AmountEntryOption } from '../enumerations/amount-entry-option';
import { BuildLineTenderProps, buildTenderAddDTO } from './build-line-tender';
import { submitPayment } from './handle-tender-payment';
import { showTenderingError } from './show-tendering-error';

export const processTenderAmountEntryOption = async (
  tender: ITender,
  amountDue: number,
): Promise<void> => {
  const selectedTenderAmountEntryOption =
    tender.amountEntryRequired || tender.paymentOptions?.amountEntryOption;

  if (!selectedTenderAmountEntryOption) {
    popupMessage(
      new EmeraldErrorModal({
        message: `InvalidPaymentOptions`,
        messageArgs: { arg: tender?.description || '' },
      }),
    );
    return;
  }

  if (selectedTenderAmountEntryOption === AmountEntryOption.No) {
    await updateTransactionStatus(CartStatus.Tender);
    const { allowPartialPaymentFromKeypad } = getCurrentXLRConfig();
    const amount =
      allowPartialPaymentFromKeypad && store.getState().salesKeypad.itemCode
        ? parseInt(store.getState().salesKeypad.itemCode)
        : amountDue;
    return submitPayment(amount, EntryMethod.Tapped, tender).finally(() => {
      resetSalesKeypadState();
    });
  }

  const tenderProps: BuildLineTenderProps = {
    tender,
    amount: amountDue,
    lineItemEntryMethod: EntryMethod.Automatic,
  };

  return preMakeTenderPayment(buildTenderAddDTO(tenderProps))
    .then(() => {
      switch (selectedTenderAmountEntryOption) {
        case AmountEntryOption.YesWithDefault: {
          const unPaidPretender = store.getState().tenderMenu.unPaidPreTender;
          showProcessTender({
            defaultValue: unPaidPretender?.amount?.amount || amountDue,
            selectedTender: tender,
          });
          break;
        }
        case AmountEntryOption.YesWithoutDefault: {
          showProcessTender({ selectedTender: tender });
          break;
        }
        case AmountEntryOption.YesWithSuggestedAmounts: {
          showProcessTender({
            suggestedAmounts: getSuggestedAmounts(tender),
            selectedTender: tender,
          });
          break;
        }
      }
    })
    .catch(async err => {
      showTenderingError(err);
      if (inPaymentSession()) {
        await endPaymentSession();
      }
    });
};

export const getSuggestedAmounts = (selectedTender?: ITender): number[] | undefined => {
  const { separatePaymentOptionsObjectInTender } = getCurrentXLRConfig();

  if (separatePaymentOptionsObjectInTender) {
    const suggestedAmountsConfig = selectedTender?.paymentOptions?.suggestedAmounts;
    const upPaidPretender = store.getState().tenderMenu.unPaidPreTender;
    const amountDue = store.getState().itemList.amountDue;

    return buildSuggestedAmounts(
      upPaidPretender?.amount?.amount || amountDue,
      suggestedAmountsConfig,
    );
  } else {
    //R10 updates the redux store for suggestedAmounts using tenderSuggestedAmountLookup
    return selectedTender?.suggestedAmounts;
  }
};

export const buildSuggestedAmounts = (amountDue: number, suggestedAmounts?: number[]): number[] => {
  let newSuggestedAmounts: number[] =
    suggestedAmounts?.map(
      suggestedAmount => Math.ceil(amountDue / suggestedAmount) * suggestedAmount,
    ) || [];
  newSuggestedAmounts.sort((a, b) => a - b);
  newSuggestedAmounts.unshift(amountDue);
  newSuggestedAmounts = [...new Set(newSuggestedAmounts)];
  return newSuggestedAmounts;
};
